import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';

export type LegalSource = 'editor' | 'file';

export interface LegalDocument {
  id: number;
  key: string;
  title: string;
  source: LegalSource;
  content: string;
  file: { name: string; mime: string; size: number } | null;
  version: string;
  is_published: boolean;
  is_system: boolean;
  sort_order: number;
  updated_at: string;
}

export interface LegalDocumentLimits {
  documents: number;
  content_max: number;
  file_max_bytes: number;
  file_extensions: string[];
}

export interface LegalDocumentDraft {
  title: string;
  source: LegalSource;
  content: string;
  is_published: boolean;
  /** Archivo nuevo elegido en el panel (aún sin subir). */
  file?: File | null;
  /** Quitar el archivo guardado sin reemplazarlo. */
  remove_file?: boolean;
}

@Injectable({ providedIn: 'root' })
export class PlatformLegalDocumentsService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.apiUrl}/platform-settings/legal-documents`;

  load(): Observable<{ documents: LegalDocument[]; limits: LegalDocumentLimits }> {
    return this.http.get<{ documents: LegalDocument[]; limits: LegalDocumentLimits }>(this.url);
  }

  create(draft: LegalDocumentDraft): Observable<{ document: LegalDocument; msg: string }> {
    return this.http.post<{ document: LegalDocument; msg: string }>(this.url, this.toFormData(draft));
  }

  update(id: number, draft: LegalDocumentDraft): Observable<{ document: LegalDocument; msg: string }> {
    return this.http.put<{ document: LegalDocument; msg: string }>(`${this.url}/${id}`, this.toFormData(draft));
  }

  remove(id: number): Observable<{ msg: string }> {
    return this.http.delete<{ msg: string }>(`${this.url}/${id}`);
  }

  /** Archivo guardado (publicado o no) para la vista previa del panel. */
  downloadFile(id: number): Observable<Blob> {
    return this.http.get(`${this.url}/${id}/file`, { responseType: 'blob' });
  }

  // Siempre FormData: el contenido puede superar el límite de JSON del backend
  // y así el archivo viaja en la misma petición que el resto de los campos.
  private toFormData(draft: LegalDocumentDraft): FormData {
    const form = new FormData();
    form.append('title', draft.title);
    form.append('source', draft.source);
    form.append('content', draft.content || '');
    form.append('is_published', draft.is_published ? '1' : '0');
    if (draft.remove_file) form.append('remove_file', '1');
    if (draft.file) form.append('file', draft.file, draft.file.name);
    return form;
  }
}
